import React, { useEffect, useState } from 'react';
import { BaseWizardProps } from '../wizard/wizard-step-base';
import {
  GoACallout,
  GoAFormItem,
  GoARadioGroup,
  GoARadioItem,
  GoASpacer,
} from '@abgov/react-components';
import { AppDispatch, RootState } from '../../state/store';
import { useDispatch, useSelector } from 'react-redux';
import _, { set } from 'lodash';
import {
  defaultValidate,
  WizardButtonGroup,
} from '../wizard/wizard-button-group-component';
import { removeAllValidationErrorNotifications } from '../../state/slices/notificationSlice';
import { updateCollision } from '../../state/slices/collisionSlice';
import { Collision } from '../../models/collision';

interface IdentifyLocationProps extends BaseWizardProps {
  // Add any additional props specific to IdentifyLocationComponent here
}

const useAppDispatch = () => useDispatch<AppDispatch>();
const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected,
) => TSelected = useSelector;

export const IdentifyLocationComponent = ({
  navigationConfiguration,
}: IdentifyLocationProps) => {
  const dispatch = useAppDispatch();

  const stateCollision = useAppSelector((state: RootState) => state.collision);

  const defaultSelection = '0';
  const errorText =
    'Choose whether to use location services or not in order to find your collision location';

  const [nextPage, setNextPage] = useState('');
  const [selection, setSelection] = useState(defaultSelection);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!stateCollision.collision.location) {
      setSelection('0');
    } else if (stateCollision.collision.location!.useLocationServices) {
      setSelection('yes');
    } else {
      setSelection('no');
    }
  }, [stateCollision.collision.location]);

  useEffect(() => {
    let pageConfig = '#error';

    if (selection === 'yes') {
      pageConfig = navigationConfiguration.pagingConfiguration.find(
        (item) => item.yes !== undefined,
      )!.yes;
    } else if (selection === 'no') {
      pageConfig = navigationConfiguration.pagingConfiguration.find(
        (item) => item.no !== undefined,
      )!.no;
    }
    setNextPage(pageConfig);
  }, [selection]);

  const save = () => {
    if (selection) {
      let updatedCollision: Collision = _.cloneDeep(stateCollision.collision);

      updatedCollision.location = {
        ...stateCollision.collision.location!,
        useLocationServices: selection === 'yes',
      };

      if (
        navigationConfiguration.updateMetadata &&
        navigationConfiguration.stepId
      ) {
        const stepUpdated = navigationConfiguration.updateMetadata(
          updatedCollision,
          navigationConfiguration.stepId!,
          navigationConfiguration.stageCompleted
            ? navigationConfiguration.stageCompleted
            : false,
          !navigationConfiguration.stageCompleted,
        );

        if (stepUpdated && updatedCollision.metadata) {
          updatedCollision.metadata = {
            ...updatedCollision.metadata,
            formState: stepUpdated,
          };
        }
      }

      dispatch(updateCollision(updatedCollision));
    }

    if (navigationConfiguration.save) {
      navigationConfiguration.save();
    }
  };

  const navigate = () => {};
  return (
    <div>
      {/* Your component implementation goes here */}
      <h2 className="location-screen">Identify your collision location</h2>
      You can use your device's location services to identify your collision
      location.
      <GoASpacer vSpacing={'l'} />
      <GoACallout heading="Important" type="information">
        <p>
          If you're using a mobile device at the scene, make sure location
          services are turned on in your device settings
        </p>
      </GoACallout>
      <GoASpacer vSpacing={'xl'} />
      <GoAFormItem
        label="Do you want Report My Collision to find your collision location now?"
        error={formError}
      >
        <GoARadioGroup
          name="findLocationGroup"
          value={selection}
          error={formError.length > 0}
          onChange={(item, id) => {
            setSelection(id);
            setFormError('');
            dispatch(removeAllValidationErrorNotifications());
          }}
        >
          <GoARadioItem
            value="yes"
            label="Yes - help me find my collision location"
          ></GoARadioItem>
          <GoARadioItem
            value="no"
            label="No - I will manually search for the collision location myself"
          ></GoARadioItem>
        </GoARadioGroup>
      </GoAFormItem>
      <WizardButtonGroup
        nextPage={nextPage}
        validate={(): boolean => {
          return defaultValidate(
            selection,
            defaultSelection,
            errorText,
            setFormError,
            navigationConfiguration.errorNotification,
          );
        }}
        goToReview={navigationConfiguration.goToReview}
        save={save}
        navigate={navigationConfiguration.navigate}
      ></WizardButtonGroup>
    </div>
  );
};
